// What people said, on the same page as how the opponent is being trained.
//
// **One dashboard rather than two**, because the two questions are asked in the
// same sitting: somebody checking on a search that has been running overnight is
// the same person who wants to know whether anybody hit a wall in the game. The
// tuner page already had the gate, the polling and the surface, so this is a
// section of it rather than a second private page with a second password.
//
// **Acknowledging is the whole interaction.** A list of reports with no way to
// mark one read is a list that is read from the top every time and acted on
// never — the state that matters is not "what has arrived" but "what have I not
// dealt with yet".

import { useCallback, useEffect, useState } from "react";

import { FeedbackBoard } from "./FeedbackBoard";
import { Btn, Notice, Panel, Pill, Segmented, styled, Text } from "./kit";

/** One report, as `server/feedback.ts` lists it. */
export interface Filed {
  id: number;
  at: number;
  message: string;
  build: string | null;
  mode: string | null;
  room: string | null;
  hash: string | null;
  ackedAt: number | null;
  hasState: boolean;
  /** How many commands came with it. Zero means a board with no journey to it. */
  logLength: number;
}

export interface Counts {
  open: number;
  total: number;
}

/** When, in words, on the same clock the rest of the page uses. */
function when(at: number, now: number): string {
  const seconds = Math.max(0, Math.round((now - at) / 1000));
  if (seconds < 90) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/**
 * One report's plate.
 *
 * Acknowledged reports stay legible and stop competing: the border is what says
 * "dealt with", not the text going grey enough to be a rendering fault.
 */
const Plate = styled(Panel, {
  shouldForwardProp: (prop: string) => prop !== "acked"
})<{ acked?: boolean }>(({ theme, acked }) => ({
  padding: theme.kit.space.md,
  borderColor: acked
    ? theme.kit.color.line
    : `color-mix(in srgb, ${theme.kit.color.brand} 45%, ${theme.kit.color.line})`,
  opacity: acked ? 0.6 : 1
}));

const Head = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: theme.kit.space.sm,
  marginBottom: theme.kit.space.sm
}));

const Spacer = styled("div")({ flex: 1 });

/**
 * The message. `pre-wrap`, because the whole point of the control that produced
 * it was that a bug report is a paragraph — collapsing the newlines here would
 * throw away the shape the person typed.
 */
const Message = styled(Text)({ display: "block", whiteSpace: "pre-wrap", lineHeight: 1.55 });

const Column = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.kit.space.sm
}));

function Report({
  report,
  now,
  onAck,
  open,
  onOpen
}: {
  report: Filed;
  now: number;
  onAck: (id: number, acked: boolean) => void;
  open: boolean;
  onOpen: (id: number | null) => void;
}): React.ReactElement {
  const acked = report.ackedAt !== null;
  return (
    <Plate data-testid="feedback-report" acked={acked}>
      <Head>
        <Text tone="dim" size={11}>
          {when(report.at, now)}
        </Text>
        {report.mode && (
          <Pill>
            <Text size={11}>{report.mode}</Text>
          </Pill>
        )}
        {report.build && (
          <Text role="display" tone="dim" size={10}>
            {report.build}
          </Text>
        )}
        {/* **The board is the reason this feature exists**, so whether one came
            with the report is the first thing worth seeing about it. */}
        {report.hasState && (
          <Btn
            small
            variant="ghost"
            data-testid="feedback-open-board"
            onClick={() => onOpen(open ? null : report.id)}
          >
            {open ? "hide board" : report.logLength > 0 ? `play ${report.logLength}` : "board"}
          </Btn>
        )}
        <Spacer />
        <Btn
          small
          variant={acked ? "ghost" : "default"}
          data-testid={acked ? "feedback-unack" : "feedback-ack"}
          onClick={() => onAck(report.id, !acked)}
        >
          {acked ? "Undo" : "Acknowledge"}
        </Btn>
      </Head>
      <Message size={13}>{report.message}</Message>
      {/* Mounted only while open, so nothing is fetched, decoded or replayed
          for a report nobody has asked to look at — the list is the common
          case and a board is half a megabyte. */}
      {open && <FeedbackBoard id={report.id} onClose={() => onOpen(null)} />}
    </Plate>
  );
}

const Bar = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: theme.kit.space.md,
  marginBottom: theme.kit.space.lg
}));

const Section = styled("div")(({ theme }) => ({ marginTop: theme.kit.space.xl }));

const FILTERS = [
  { value: "open" as const, label: "Waiting", testId: "feedback-filter-open" },
  { value: "all" as const, label: "Everything", testId: "feedback-filter-all" }
];

/**
 * The reports, and the one thing to do with them.
 *
 * Fetched rather than pushed: the tuner digest arrives over a socket because it
 * changes every few seconds and every viewer wants the same copy, and neither is
 * true here — feedback arrives a few times a day and this is a list somebody
 * refreshes when they sit down to read it.
 */
export function FeedbackPanel({ now }: { now: number }): React.ReactElement {
  const [reports, setReports] = useState<Filed[]>([]);
  const [counts, setCounts] = useState<Counts>({ open: 0, total: 0 });
  const [openOnly, setOpenOnly] = useState(true);
  const [state, setState] = useState<"loading" | "ready" | "failed" | "unconfigured">("loading");
  /**
   * Which report has its board open — one at a time.
   *
   * Held as the **id** rather than the report, for the reason the gallery holds
   * a game id: the list is refetched, and holding the object pins the panel to
   * a copy that is no longer in it.
   */
  const [opened, setOpened] = useState<number | null>(null);

  const load = useCallback((all: boolean) => {
    void fetch(`/api/feedback${all ? "?all=1" : ""}`, { credentials: "same-origin" })
      .then(async (response) => {
        if (response.status === 503) {
          setState("unconfigured");
          return;
        }
        if (!response.ok) {
          setState("failed");
          return;
        }
        const body = (await response.json()) as { reports?: Filed[]; counts?: Counts };
        setReports(body.reports ?? []);
        setCounts(body.counts ?? { open: 0, total: 0 });
        setState("ready");
      })
      .catch(() => setState("failed"));
  }, []);

  useEffect(() => load(!openOnly), [load, openOnly]);

  const ack = (id: number, acked: boolean): void => {
    // **Moved in the list before the server answers**, and put back if it
    // refuses. This is a list somebody works down quickly; a round trip between
    // the click and the row changing makes it feel broken and invites a second
    // click on the same row.
    setReports((was) =>
      was.map((report) =>
        report.id === id ? { ...report, ackedAt: acked ? Date.now() : null } : report
      )
    );
    setCounts((was) => ({ ...was, open: Math.max(0, was.open + (acked ? -1 : 1)) }));
    void fetch("/api/feedback/ack", {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ id, acked })
    })
      .then((response) => {
        if (!response.ok) load(!openOnly);
      })
      .catch(() => load(!openOnly));
  };

  if (state === "unconfigured") {
    return (
      <Notice data-testid="feedback-unconfigured" style={{ marginTop: 16 }}>
        This deployment has no feedback database configured, so nothing is being collected. Set{" "}
        <code>DATABASE_URL</code> to turn it on.
      </Notice>
    );
  }

  return (
    <Section data-feedback-panel>
      <Bar>
        <Text weight={700} size={16}>
          Feedback
        </Text>
        <Text
          role="display"
          tone={counts.open > 0 ? "brand" : "dim"}
          weight={700}
          size={22}
          data-testid="feedback-open-count"
        >
          {counts.open}
        </Text>
        <Text tone="dim" size={12}>
          waiting, {counts.total} in all
        </Text>
        <Spacer />
        <Segmented
          small
          value={openOnly ? "open" : "all"}
          options={FILTERS}
          onChange={(value) => setOpenOnly(value === "open")}
        />
        <Btn small variant="ghost" data-testid="feedback-refresh" onClick={() => load(!openOnly)}>
          Refresh
        </Btn>
      </Bar>

      {state === "failed" && (
        <Notice tone="bad" data-testid="feedback-list-failed" style={{ marginBottom: 16 }}>
          Could not read the reports. The database may be down — nothing has been lost, this view
          just cannot see it.
        </Notice>
      )}

      {state === "ready" && reports.length === 0 && (
        <Notice data-testid="feedback-empty">
          {openOnly
            ? "Nothing waiting. Everything that has come in has been acknowledged."
            : "No reports yet."}
        </Notice>
      )}

      <Column>
        {reports.map((report) => (
          <Report
            key={report.id}
            report={report}
            now={now}
            onAck={ack}
            open={opened === report.id}
            onOpen={setOpened}
          />
        ))}
      </Column>
    </Section>
  );
}
